import { autocomplete, getAlgoliaResults } from '@algolia/autocomplete-js'
import { liteClient as algoliasearch } from 'algoliasearch/lite'
import { debouncePromise } from '../../../js/utilities/functions/debounce.js'

const QUERY_MINIMUM_LENGTH = 2
const HITS_PER_PAGE = 5
const DEBOUNCE_MS = 150

const getLocalizedUrl = (url, locale) => {
  return locale === 'fr_BE' ? `/fr${url}` : url
}

const renderDocument = ({ item, components, html, locale }) => html`
  <a href="${getLocalizedUrl(item.url, locale)}" class="aa-ItemLink text-decoration-none">
    <div class="aa-ItemContent d-flex align-items-center gap-3">
      <div class="document-search-image flex-shrink-0">
        <img src="${item.image}" alt="" width="56" height="56" class="w-100 h-100 object-fit-contain" />
      </div>

      <div class="aa-ItemContentBody overflow-hidden">
        <div class="aa-ItemContentTitle fw-semibold text-body">
          ${components.Highlight({
            hit: item,
            attribute: 'content_title',
          })}
        </div>

        <div class="aa-ItemContentDescription text-body-secondary mt-1">
          ${components.Snippet({
            hit: item,
            attribute: 'content',
          })}
        </div>
      </div>

      <i class="ci-arrow-right text-body-secondary flex-shrink-0 ms-auto"></i>
    </div>
  </a>
`

const init = () => {
  const container = document.querySelector('[data-document-search]')

  if (!(container instanceof HTMLElement)) {
    return
  }

  const applicationConfig = window.polyestershoppen

  if (!applicationConfig?.algolia?.indexes?.document) {
    return
  }

  const { appId, apiKey, indexes } = applicationConfig.algolia
  const { locale } = applicationConfig

  const { placeholder, cancelButton, resultsTitle, noResults } = container.dataset

  const searchClient = algoliasearch(appId, apiKey)
  const debouncedSources = debouncePromise((sources) => Promise.resolve(sources), DEBOUNCE_MS)

  container.querySelector('[data-document-search-placeholder]')?.remove()

  autocomplete({
    container,
    placeholder,
    stallThreshold: 400,
    detachedMediaQuery: 'none',
    panelPlacement: 'input-wrapper-width',
    openOnFocus: false,
    insights: true,

    classNames: {
      root: 'w-100 document-search-autocomplete',
      form: 'bg-body border rounded-3 shadow-none',
      input: 'text-body',
      panel: 'document-search-panel bg-body border rounded-3 shadow-lg',
      sourceHeader: 'border-0',
    },

    translations: {
      detachedCancelButtonText: cancelButton,
      clearButtonTitle: cancelButton,
    },

    getSources: ({ query }) => {
      if (query.trim().length < QUERY_MINIMUM_LENGTH) {
        return []
      }

      return debouncedSources([
        {
          sourceId: 'documents',

          getItems: () => {
            return getAlgoliaResults({
              searchClient,
              queries: [
                {
                  indexName: indexes.document,
                  params: {
                    query,
                    hitsPerPage: HITS_PER_PAGE,
                    clickAnalytics: true,
                  },
                },
              ],
            })
          },

          getItemUrl: ({ item }) => {
            return getLocalizedUrl(item.url, locale)
          },

          templates: {
            header: ({ html }) => html`
              <div class="d-flex align-items-center gap-2 px-2 pt-2">
                <span class="fs-sm fw-semibold text-body">${resultsTitle}</span>
                <hr class="flex-grow-1 my-0" />
              </div>
            `,

            item: ({ item, components, html }) => {
              return renderDocument({
                item,
                components,
                html,
                locale,
              })
            },

            noResults: ({ html }) => html`
              <div class="text-center text-body-secondary py-4 px-3">
                <i class="ci-search fs-3 d-block mb-2"></i>
                <span>${noResults}</span>
              </div>
            `,
          },
        },
      ])
    },
  })
}

export default {
  init,
}
